/**
 * Bounding boxes for US states + DC, used to fit the map viewport when a user
 * searches a state-level query (e.g. "Texas", "Florida"). The geocoder collapses
 * a state query to a single centroid point, which is useless for fitBounds — this
 * table restores the rectangular extent the geocoder originally had.
 *
 * Coordinates are USGS state-extent values rounded to 2 decimals. They don't change.
 * Each entry is { sw: [lat, lng], ne: [lat, lng] }.
 *
 * When Canada/UK/AU businesses onboard, add a parallel table (caStateBounds.ts etc.)
 * keyed by province code, and key the lookup by countryID upstream.
 */
export interface StateBoundingBox {
    sw: [number, number];
    ne: [number, number];
}

export const US_STATE_BOUNDS: Record<string, StateBoundingBox> = {
    AL: { sw: [30.22, -88.47], ne: [35.01, -84.89] },
    AK: { sw: [51.21, -179.15], ne: [71.40, -129.97] }, // dateline-crossing simplified to mainland
    AZ: { sw: [31.33, -114.82], ne: [37.00, -109.04] },
    AR: { sw: [33.00, -94.62], ne: [36.50, -89.64] },
    CA: { sw: [32.53, -124.41], ne: [42.01, -114.13] },
    CO: { sw: [36.99, -109.06], ne: [41.00, -102.04] },
    CT: { sw: [40.99, -73.73], ne: [42.05, -71.79] },
    DE: { sw: [38.45, -75.79], ne: [39.84, -75.05] },
    DC: { sw: [38.79, -77.12], ne: [38.99, -76.91] },
    FL: { sw: [24.52, -87.63], ne: [31.00, -80.03] },
    GA: { sw: [30.36, -85.61], ne: [35.00, -80.84] },
    HI: { sw: [18.91, -160.24], ne: [22.24, -154.81] },
    ID: { sw: [41.99, -117.24], ne: [49.00, -111.04] },
    IL: { sw: [36.97, -91.51], ne: [42.51, -87.02] },
    IN: { sw: [37.77, -88.10], ne: [41.76, -84.78] },
    IA: { sw: [40.38, -96.64], ne: [43.50, -90.14] },
    KS: { sw: [36.99, -102.05], ne: [40.00, -94.59] },
    KY: { sw: [36.50, -89.57], ne: [39.15, -81.96] },
    LA: { sw: [28.93, -94.04], ne: [33.02, -88.82] },
    ME: { sw: [43.06, -71.08], ne: [47.46, -66.95] },
    MD: { sw: [37.89, -79.49], ne: [39.72, -75.05] },
    MA: { sw: [41.24, -73.51], ne: [42.89, -69.93] },
    MI: { sw: [41.70, -90.42], ne: [48.31, -82.42] },
    MN: { sw: [43.50, -97.24], ne: [49.38, -89.49] },
    MS: { sw: [30.17, -91.66], ne: [35.00, -88.10] },
    MO: { sw: [35.99, -95.77], ne: [40.61, -89.10] },
    MT: { sw: [44.36, -116.05], ne: [49.00, -104.04] },
    NE: { sw: [39.99, -104.05], ne: [43.00, -95.31] },
    NV: { sw: [35.00, -120.01], ne: [42.00, -114.04] },
    NH: { sw: [42.70, -72.56], ne: [45.31, -70.56] },
    NJ: { sw: [38.93, -75.56], ne: [41.36, -73.89] },
    NM: { sw: [31.33, -109.05], ne: [37.00, -103.00] },
    NY: { sw: [40.50, -79.76], ne: [45.02, -71.86] },
    NC: { sw: [33.84, -84.32], ne: [36.59, -75.46] },
    ND: { sw: [45.94, -104.05], ne: [49.00, -96.55] },
    OH: { sw: [38.40, -84.82], ne: [42.32, -80.52] },
    OK: { sw: [33.62, -103.00], ne: [37.00, -94.43] },
    OR: { sw: [41.99, -124.57], ne: [46.29, -116.46] },
    PA: { sw: [39.72, -80.52], ne: [42.27, -74.69] },
    RI: { sw: [41.15, -71.86], ne: [42.02, -71.12] },
    SC: { sw: [32.03, -83.35], ne: [35.22, -78.54] },
    SD: { sw: [42.48, -104.06], ne: [45.95, -96.44] },
    TN: { sw: [34.98, -90.31], ne: [36.68, -81.65] },
    TX: { sw: [25.84, -106.65], ne: [36.50, -93.51] },
    UT: { sw: [36.99, -114.05], ne: [42.00, -109.04] },
    VT: { sw: [42.73, -73.44], ne: [45.02, -71.46] },
    VA: { sw: [36.54, -83.67], ne: [39.47, -75.24] },
    WA: { sw: [45.54, -124.85], ne: [49.00, -116.92] },
    WV: { sw: [37.20, -82.64], ne: [40.64, -77.72] },
    WI: { sw: [42.49, -92.89], ne: [47.08, -86.25] },
    WY: { sw: [40.99, -111.06], ne: [45.01, -104.05] },
};
